import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button/Button';
import { DialogBody, DialogFooter } from '@/components/ui/Dialog/Dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select/Select';
import { Switch } from '@/components/ui/Switch/Switch';
import { toast } from '@/components/ui/Toast/Toast';
import { useTimeFor } from '@/hooks/useTimeFor';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils';
import { useRef } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { RecurringCalendar } from '../../../RecurringCalendar/RecurringCalendar';
import { SchedulingCalendar } from '../../../SchedulingCalendar/SchedulingCalendar';
import { useDateAndTimeStep } from './DateAndTimeStep.hooks';

interface DateAndTimeStepProps {
  portalContainerRef?: React.RefObject<HTMLElement | null>;
  onClose?: () => void;
}

export const DateAndTimeStep = ({ portalContainerRef, onClose }: DateAndTimeStepProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'scheduleDialog.steps.dateAndTimeStep' });

  const { userData } = useAuthStore();
  const { zoned } = useTimeFor('sessionTime');
  const userDataStudents = userData?.students ?? [];

  const recurringCalendarRef = useRef<HTMLDivElement | null>(null);
  const schedulingCalendarRef = useRef<HTMLDivElement | null>(null);

  const {
    form,
    onSubmit,
    data,
    recurringEnabled,
    repeatOptions,
    endDateOptions,
    isCustomEndDate,
    goToPrevStep,
    maxRecurringDate,
    isBookingLoading,
    isContinueDisabled,
    canGoBack,
  } = useDateAndTimeStep();

  const studentSessionDetails = data?.studentSessionDetails ?? [];

  const studentsConfig = data?.config?.data?.students ?? [];

  const selectedUserDataStudents = userDataStudents.filter((student) => data.selectedStudentsIds.includes(student.id)) ?? [];

  const selectedCentersConfigs = studentSessionDetails.map((student) => student.centerDetails);

  const allCentersRecurringEnabled = selectedCentersConfigs.every((config) => config?.recurring_enabled);

  if (!maxRecurringDate && allCentersRecurringEnabled) {
    throw new Error('Missing max recurring date');
  }

  const selectedDate = form.getValues('dateAndTime');

  const handleFieldErrors = (
    errors: FieldErrors<{
      dateAndTime: string;
      recurringEnabled: boolean;
      recurrencePattern?: string;
      endDate?: string;
      customEndDate?: string;
    }>,
  ) => {
    if (errors.dateAndTime && errors.dateAndTime.ref) {
      toast({
        variant: 'destructive',
        description: t('validationError.noDateSelected'),
      });
      schedulingCalendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    if (errors.recurringEnabled || errors.recurrencePattern || errors.endDate) {
      return;
    }

    if (errors.customEndDate && errors.customEndDate.ref) {
      toast({
        variant: 'destructive',
        description: t('validationError.customEndDate'),
      });
      recurringCalendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }
  };

  return (
    <>
      <DialogBody>
        <div className='mb-6'>
          <h3 className='text-heading-m-bold text-text-default-primary'>{t('title')}</h3>
        </div>
        <Form {...form}>
          <form
            id='date-and-time-form'
            onSubmit={(e) =>
              void form.handleSubmit(onSubmit, (errors) => {
                handleFieldErrors(errors);
              })(e)
            }
            className='flex flex-1 flex-col'
          >
            <FormField
              control={form.control}
              name='dateAndTime'
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <div ref={schedulingCalendarRef}>
                        <SchedulingCalendar
                          value={field.value}
                          onChange={field.onChange}
                          students={selectedUserDataStudents}
                          onDialogClose={onClose}
                          studentSessionDetails={studentSessionDetails}
                          studentsConfig={studentsConfig}
                          isRecurring={recurringEnabled}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                );
              }}
            />

            {allCentersRecurringEnabled && selectedDate && (
              <div className='space-y-4 pt-8 pb-6'>
                <FormField
                  control={form.control}
                  name='recurringEnabled'
                  render={({ field }) => (
                    <FormItem>
                      <div className='flex items-center justify-between'>
                        <div className='flex flex-col gap-1'>
                          <FormLabel className='font-semibold'>{t('recurringSwitch.label')}</FormLabel>
                          <p className='text-text-default-secondary max-w-[80%]'>{t('recurringSwitch.description')}</p>
                        </div>
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} showStateLabel />
                        </FormControl>
                      </div>
                    </FormItem>
                  )}
                />

                {recurringEnabled && (
                  <div className='space-y-4'>
                    <FormField
                      control={form.control}
                      name='recurrencePattern'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel
                            className={cn('text-text-default-secondary', {
                              'text-text-error-primary': !!form.formState.errors.recurrencePattern,
                            })}
                          >
                            {t('repeatLabel')}
                          </FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger ref={field.ref} isError={!!form.formState.errors.recurrencePattern}>
                                <SelectValue placeholder={t('selectPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent portalContainer={portalContainerRef?.current}>
                                {repeatOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='endDate'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel
                            className={cn('text-text-default-secondary', {
                              'text-text-error-primary': !!form.formState.errors.endDate,
                            })}
                          >
                            {t('endDateLabel')}
                          </FormLabel>
                          <FormControl>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <SelectTrigger ref={field.ref} isError={!!form.formState.errors.endDate}>
                                <SelectValue placeholder={t('selectPlaceholder')} />
                              </SelectTrigger>
                              <SelectContent portalContainer={portalContainerRef?.current} side={isCustomEndDate ? 'bottom' : 'top'}>
                                {endDateOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {isCustomEndDate && (
                      <FormField
                        control={form.control}
                        name='customEndDate'
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <RecurringCalendar
                                value={field.value}
                                onChange={field.onChange}
                                maxRecurringDate={maxRecurringDate!}
                                firstSessionDate={zoned(selectedDate)}
                              />
                            </FormControl>
                            <div ref={recurringCalendarRef}>
                              <FormMessage className='mt-3' />
                            </div>
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
        </Form>
      </DialogBody>
      <DialogFooter>
        {canGoBack && (
          <Button type='button' variant='text-secondary' onClick={goToPrevStep} className='flex-1'>
            <ChevronLeft className='text-text-default-secondary size-5' />
            <span>{t('backButton.label')}</span>
          </Button>
        )}
        <Button type='submit' form='date-and-time-form' className='flex-1' isLoading={isBookingLoading} disabled={isBookingLoading || isContinueDisabled}>
          {t('continueButton.label')}
        </Button>
      </DialogFooter>
    </>
  );
};
